import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { NEVER, Observable, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '@env/environment';

import { LanguageStore } from '@core/language/language.store';
import { Language } from '@core/i18n/domain/language';
import { PlatformService } from '@core/platform/platform.service';
import { withLanguage } from './accept-language.context';
import { AuthSessionStore } from '@core/auth/application/auth-session.store';
import { AlertService } from '@shared/feedback/alert/alert.service';
import { ApiEnvelope, ApiResult, isApiEnvelope, unwrapEnvelope, unwrapWithMessage } from './api-envelope';
import { businessError, toApiError, ApiError } from './api-error';

/** Per-request knobs for {@link AppHttp}. */
export interface RequestOptions {
  /** Query params (null/undefined entries are dropped). */
  params?: Record<string, string | number | boolean | null | undefined> | HttpParams;
  /**
   * Skip the bearer token for this call. Use on the PUBLIC endpoints
   * (`/api/v3/auth/otp/*`, `/api/v3/subscription/plans`).
   */
  anonymous?: boolean;
  /** Extra request headers (e.g. `Idempotency-Key` on `POST /api/v3/bookings`). */
  headers?: Record<string, string>;
  /**
   * Suppress the automatic error toast for this call. Use on graceful-fallback
   * READS (they recover to mock/cache) and best-effort fire-and-forget writes,
   * so a recovered failure never nags the user. The error is still normalised
   * and rejected as a {@link ApiError} for the caller's own handling.
   */
  silent?: boolean;
  /**
   * Optional request body for a {@link AppHttp.delete} call — some endpoints take
   * their target in the body (e.g. `DELETE /api/v3/profile/favorites { type, id }`).
   * Ignored by the other verbs (they carry their body as an explicit argument).
   */
  body?: unknown;
}

/**
 * AppHttp — the single HTTP chokepoint for every /v2 backend call.
 *
 * On top of attaching the v2 bearer + `Accept-Language` and unwrapping the
 * `{ status, message, data }` envelope, it owns ERROR HANDLING:
 *   · a business error returned as HTTP 200 + `{ status:false, message }` is
 *     detected and turned into a real error (it is NOT a success),
 *   · every failure is normalised to a {@link ApiError} whose `message` is read
 *     FROM THE API (envelope/Laravel `message`, already localized) and falls back
 *     to a current/default message per status when the API gives none, and
 *   · the message is surfaced as a toast via {@link AlertService} unless the call
 *     opts out with `{ silent: true }`.
 * On `401` the session is cleared (a root effect then routes to sign-in).
 *
 * SSR-safe: on the server `AuthSessionStore.token()` is null (anonymous calls)
 * and toasts are browser-only.
 */
/**
 * Hard ceiling for any single /v2 request. Past this the request is aborted and
 * rejected as a timeout {@link ApiError} ("an error occurred, try again")
 * instead of hanging on an endless spinner.
 */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * The static app token the backend requires on every call. Normally attached by
 * {@link headersInterceptor} (see `v2-headers.interceptor.ts`); duplicated here
 * (exported so that interceptor is the only OTHER v2 reader — no third copy)
 * because {@link AppHttp.postStream} uses a raw `fetch` that bypasses the
 * interceptor chain entirely. This is still a real, open duplication across the
 * v1/v2 boundary against the legacy `headers.interceptor.ts` literal — see
 * docs/v2-isolation/ESCALATIONS.md E2, not yet resolved.
 */
export const TALBINAH_TOKEN = 's4kl2fC852tSpczXsdAJIH6fORLbgG4zfwVJVjLlolop74kUUyT0aYRxZSGAQXRB';

/**
 * The static device-identification headers /v2 attaches to every request
 * (alongside {@link TALBINAH_TOKEN}). Exported for the same reason as the
 * token above — {@link headersInterceptor} is the only other v2 reader, so a
 * value changed here can't drift out of sync with a hand-typed second copy.
 */
export const TALBINAH_DEVICE_HEADERS = {
  'Device-Type': 'web',
  'web-type': 'portal',
  platform: 'portal',
} as const;

/**
 * Parse ONE SSE block (`event: <name>` + one-or-more `data:` lines) into a frame.
 * `:`-prefixed comment/keep-alive lines are ignored; a block with no data (and no
 * explicit event) yields null. Multi-line `data:` is joined with '\n' per the SSE
 * spec. Used only by {@link AppHttp.postStream}.
 */
function parseSseBlock(block: string): { event: string; data: string } | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue; // blank / comment (keep-alive)
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  if (!dataLines.length && event === 'message') return null;
  return { event, data: dataLines.join('\n') };
}

@Service()
export class AppHttp {
  private readonly http = inject(HttpClient);
  private readonly lang = inject(LanguageStore);
  private readonly session = inject(AuthSessionStore);
  private readonly platform = inject(PlatformService);
  private readonly alerts = inject(AlertService);
  private readonly apiBase = environment.apiUrl;

  /**
   * Depth of the {@link allowServerReads} scope currently open. `0` — the normal
   * state, and the state of every call site that does not opt in — keeps the SSR
   * gate in {@link process} exactly as it was.
   */
  private serverReadDepth = 0;

  /**
   * Run `build` with the SSR gate in {@link process} LIFTED, so the reads it
   * constructs are dispatched for real on the server instead of resolving to
   * `NEVER`.
   *
   * The gate below is deliberately blunt, because it protects the render from an
   * entire class of failure. This is the one documented way past it, and it exists
   * for exactly one need: metadata that must be in the RAW server-rendered html —
   * a `<title>`, description and OpenGraph card built from the real entity rather
   * than a resource-typed placeholder. A crawler and a link unfurl never run the
   * JavaScript that would otherwise fetch it.
   *
   * Using it safely is the caller's job, and there are three rules:
   *
   *   1. AWAIT the read. The crash the gate was added for came from fire-and-forget
   *      reads (`void facade.load()`): the render finished, then the aborted fetch
   *      rejected with no owner. An awaited read has an owner by construction.
   *   2. CATCH it, and fall back to something honest. Off-prod the API is
   *      IP-allowlisted and unreachable from the render host, and in production
   *      these endpoints answer anonymously with 404 — so "no data" is the normal
   *      outcome, not an exception.
   *   3. BOUND it. Pass a timeout; a render must never wait on a socket.
   *
   * `CareDetailSeoService` is the only caller and shows all three.
   *
   * Scope-based rather than a per-request flag because {@link process} resolves the
   * gate when the observable is CONSTRUCTED (it returns `NEVER` eagerly), which is
   * synchronous inside `build`. In SSR each request gets its own injector, so this
   * instance — and this counter — is per-render; concurrent renders cannot see each
   * other's scope. In the browser it is a straight pass-through.
   */
  allowServerReads<T>(build: () => T): T {
    if (!this.platform.isServer) return build();
    this.serverReadDepth++;
    try {
      return build();
    } finally {
      this.serverReadDepth--;
    }
  }

  get<T>(path: string, opts: RequestOptions = {}): Observable<T> {
    return this.process<T>(this.http.get<ApiEnvelope<T> | T>(this.url(path), this.httpOptions(opts)), opts);
  }

  post<T>(path: string, body: unknown, opts: RequestOptions = {}): Observable<T> {
    return this.process<T>(this.http.post<ApiEnvelope<T> | T>(this.url(path), body ?? {}, this.httpOptions(opts)), opts);
  }

  /**
   * POST that also hands back the envelope's success `message` — for a write whose
   * confirmation toast should be the server's own text. Same error handling as {@link post}.
   */
  postWithMessage<T>(path: string, body: unknown, opts: RequestOptions = {}): Observable<ApiResult<T>> {
    return this.process<T, ApiResult<T>>(
      this.http.post<ApiEnvelope<T> | T>(this.url(path), body ?? {}, this.httpOptions(opts)),
      opts,
      unwrapWithMessage,
    );
  }

  put<T>(path: string, body: unknown, opts: RequestOptions = {}): Observable<T> {
    return this.process<T>(this.http.put<ApiEnvelope<T> | T>(this.url(path), body ?? {}, this.httpOptions(opts)), opts);
  }

  patch<T>(path: string, body: unknown, opts: RequestOptions = {}): Observable<T> {
    return this.process<T>(this.http.patch<ApiEnvelope<T> | T>(this.url(path), body ?? {}, this.httpOptions(opts)), opts);
  }

  delete<T>(path: string, opts: RequestOptions = {}): Observable<T> {
    // Angular's `HttpClient.delete` accepts an optional `body` in its options —
    // attach it when the caller provides one (query-string params still apply).
    const options = opts.body !== undefined ? { ...this.httpOptions(opts), body: opts.body } : this.httpOptions(opts);
    return this.process<T>(this.http.delete<ApiEnvelope<T> | T>(this.url(path), options), opts);
  }

  /**
   * Fetch a binary body (an authenticated byte-stream — e.g. a server-rendered
   * PDF whose endpoint requires the bearer, so `window.open` would 401). Attaches
   * the bearer + `Accept-Language` like every other call but does NOT unwrap the
   * JSON envelope; the caller reads the {@link Blob} (open an object URL, download,
   * …). Failures normalise to a {@link ApiError} (silent by default here — the
   * caller owns its own error UX). SSR-safe: no token on the server.
   */
  getBlob(path: string, opts: RequestOptions = {}): Observable<Blob> {
    // Same SSR gate as `process` — a byte-stream download is browser-only by nature
    // (it ends in an object URL / an `<a download>` click), and it bypasses `process`
    // because it must not unwrap the JSON envelope.
    if (this.platform.isServer) return NEVER;
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };
    if (!opts.anonymous) {
      const token = this.session.token();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    return this.http
      .get(this.url(path), {
        headers,
        params: this.toParams(opts.params),
        context: withLanguage(this.lang.current()),
        responseType: 'blob',
      })
      .pipe(
        timeout({ each: REQUEST_TIMEOUT_MS }),
        catchError((err: unknown) => {
          const apiErr = err instanceof ApiError ? err : toApiError(err, this.lang.current());
          if (apiErr.httpStatus === 401) this.session.clear();
          if (!opts.silent && this.platform.isBrowser) this.alerts.error(apiErr.message);
          return throwError(() => apiErr);
        }),
      );
  }

  /**
   * POST a JSON body and consume a **Server-Sent-Events** response as an async
   * stream of `{ event, data }` frames (used by the Khoyek chat's streaming reply,
   * `/messages/stream`). Unlike the RxJS JSON methods this uses a raw `fetch`
   * (needed for an incrementally-read body), so it bypasses the interceptor chain
   * and re-attaches the SAME standard headers the global `headersInterceptor`
   * would add (bearer · Accept-Language · talbinah-token · platform).
   *
   * Browser-only: it throws on the server. The caller MUST treat ANY throw / early
   * termination as "stream unavailable" and fall back to the non-streaming
   * endpoint — a raw failure is never surfaced to the user (spec §5.2). Does NOT
   * unwrap the JSON envelope; SSE bodies are raw event blocks.
   */
  async *postStream(
    path: string,
    body: unknown,
    opts: { signal?: AbortSignal } = {},
  ): AsyncGenerator<{ event: string; data: string }, void, unknown> {
    if (!this.platform.isBrowser) throw new ApiError(0, 'SSE unavailable (SSR)');
    const lang = this.lang.current() === Language.Ar ? Language.Ar : Language.En;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Accept-Language': lang,
      'talbinah-token': TALBINAH_TOKEN,
      ...TALBINAH_DEVICE_HEADERS,
    };
    const token = this.session.token();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(this.url(path), {
      method: 'POST',
      headers,
      body: JSON.stringify(body ?? {}),
      signal: opts.signal,
    });
    if (!res.ok || !res.body) throw new ApiError(res.status || 0, `SSE HTTP ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        // Normalise CRLF so the blank-line frame separator is always '\n\n'.
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const frame = parseSseBlock(block);
          if (frame) yield frame;
        }
      }
      // Flush any trailing frame not terminated by a blank line.
      const tail = parseSseBlock(buffer);
      if (tail) yield tail;
    } finally {
      try {
        await reader.cancel();
      } catch {
        /* reader already closed — ignore */
      }
    }
  }

  // ─── internals ────────────────────────────────────────────────────────
  /** Unwrap success, convert `status:false` to an error, normalise + surface failures. */
  private process<T, R = T>(
    obs: Observable<ApiEnvelope<T> | T>,
    opts: RequestOptions,
    unwrap: (res: ApiEnvelope<T> | T) => R = unwrapEnvelope as (res: ApiEnvelope<T> | T) => R,
  ): Observable<R> {
    // ── SSR: the server never calls the API ──────────────────────────────────
    //
    // This is THE gate that makes a server render safe, and it is here rather than
    // sprinkled through the facades so it cannot be forgotten by the next feature.
    //
    // The v3 API is bearer-authenticated and the render process has no session
    // (the token lives in localStorage), so every call it could make would 401.
    // Worse, they are actively harmful: off-prod the API is IP-allowlisted and
    // UNREACHABLE from the render host, so a request hangs until it connect-times-out
    // and the aborted in-flight fetch then rejects with `undefined` — which crashes
    // the render, and under Vite crashes the error logger after it (the
    // `ɵgetOrCreateAngularServerApp is not a function` cascade documented in
    // `app.routes.server.ts`). Fire-and-forget reads (`void facade.load()`) are the
    // most dangerous of all: nothing awaits them, so the render finishes and the
    // abort lands with no owner.
    //
    // `NEVER` — not `EMPTY`, not an error. It neither emits nor completes, so:
    //   · no request is ever dispatched (the HttpClient observable stays cold —
    //     it is only constructed as an argument, never subscribed);
    //   · `firstValueFrom` neither resolves NOR rejects, so no caller's `catch`
    //     runs and no `ActionState` flips to an error state (`EMPTY` would reject
    //     with "no elements in sequence" and paint spurious error screens);
    //   · every `loading` flag stays true, so the server renders each screen's
    //     EXISTING skeleton — which is byte-for-byte what the browser renders at
    //     t=0 before its own reads land. That identity is what makes hydration
    //     clean; the browser then fetches for real and fills the skeletons in.
    //
    // The result is a server render that issues ZERO http calls: it cannot hang,
    // cannot crash, and behaves identically on localhost, dev and production.
    //
    // The ONE documented exception is a read constructed inside
    // {@link allowServerReads} — SEO metadata that has to be in the raw server html.
    // It falls through to the normal pipeline below, minus the toast (which is
    // already browser-only), and its caller owns the await/catch/timeout contract
    // that keeps the guarantees above intact. Everything else still gets `NEVER`.
    if (this.platform.isServer && this.serverReadDepth === 0) return NEVER;

    return obs.pipe(
      timeout({ each: REQUEST_TIMEOUT_MS }), // abort hung requests → TimeoutError → friendly toast below
      map((res) => {
        if (isApiEnvelope<T>(res) && res.status === false) {
          throw businessError(res, this.lang.current()); // HTTP 200 business error
        }
        return unwrap(res);
      }),
      catchError((err: unknown) => {
        const apiErr = err instanceof ApiError ? err : toApiError(err, this.lang.current());
        if (apiErr.httpStatus === 401) this.session.clear(); // no refresh token — re-auth
        if (!opts.silent && this.platform.isBrowser) this.alerts.error(apiErr.message);
        return throwError(() => apiErr);
      }),
    );
  }

  private url(path: string): string {
    if (/^https?:\/\//.test(path)) return path;
    return `${this.apiBase}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  private httpOptions(opts: RequestOptions) {
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };
    if (!opts.anonymous) {
      const token = this.session.token();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    return {
      headers,
      params: this.toParams(opts.params),
      context: withLanguage(this.lang.current()),
    };
  }

  private toParams(
    params?: Record<string, string | number | boolean | null | undefined> | HttpParams,
  ): HttpParams | undefined {
    if (!params) return undefined;
    if (params instanceof HttpParams) return params;
    let p = new HttpParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined) p = p.set(k, String(v));
    }
    return p;
  }
}
